import { useCallback, useEffect, useMemo } from "react"
import { useDispatch, useSelector } from "react-redux"
import moment from "moment"
import { getToday, Period } from "helpers/dates"
import { DataStatus, LOADING } from "helpers/data-statuses"
import { getBatchMomentOrTimeOnly, isBatchNewerThanPricing } from "helpers/intraday"
import { isPinnedHistoricalBatch } from "gatherers/helpers"
import { downloadXlsx } from "helpers/csv"
import { Creators as DialogCreators } from "modules/dialog/model"
import { IsoDate } from "helpers/types/dates"
import type { DownloadSheet } from "connected-components/dashboard/context/download-registry"
import { useIsDashboardOptimizeExperimentsFeatureEnabled } from "resources/account"
import { getIsIntradayAnalyticsEnabled } from "selectors/features"
import { InstrumentWithMetadata } from "helpers/types/instruments/metadata"
import { IntradayBatchMetadata } from "model/state"

/**
 * Custom hook to handle intraday data refresh workflow
 * Automatically refreshes data when appropriate, or shows manual refresh button
 */
export function useIntradayRefresh(
  isIntradayEnabled: boolean,
  isIntradayBatchAvailable: boolean,
  useLatestDate: boolean,
  lastIntradayBatch: IntradayBatchMetadata,
  intradayBatchMetadata: IntradayBatchMetadata,
  setIntradayBatchMetadata: (metadata: IntradayBatchMetadata | null) => void,
  setPeriod: (period: Period | { period: Period, startDate: IsoDate, endDate: IsoDate }) => void,
  currentPeriod: Period,
  adjustedStartDate: IsoDate,
  // [TODO:TS] Ideally type when trades are typed, but we only care about the length
  trades: unknown[],
  resetTrades: () => void,
  onDownloadData: () => { fileName: string, data: { sheets: DownloadSheet[] } | null },
  isPreferencesLoaded: boolean,
  isEndDateToday: boolean,
  lastIntradayBatchStatus: DataStatus,
  setUseLatestDate: (useLatestDate: boolean) => void,
  setComparisonInstrument: (instrument: InstrumentWithMetadata | null) => void,
  setIsOptimizePanelExpanded: (isExpanded: boolean) => void,
  hasOptimizationComparison: boolean,
  intradayPricingAsOf?: string | null,
) {
  const dispatch = useDispatch()
  const intradayDate = getToday()
  const isOptimizeExperimentsEnabled = useIsDashboardOptimizeExperimentsFeatureEnabled()
  const isIntradayAnalyticsEnabled = useSelector(getIsIntradayAnalyticsEnabled)

  // A batch ID change doesn't imply it's newer — compare moments against live pricing too.
  const lastBatchMoment = getBatchMomentOrTimeOnly(lastIntradayBatch)
  const currentBatchMoment = getBatchMomentOrTimeOnly(intradayBatchMetadata)
  const pricingMoment = intradayPricingAsOf ? moment(intradayPricingAsOf) : null
  const currentlyShownMoment = pricingMoment && (!currentBatchMoment || pricingMoment.isAfter(currentBatchMoment))
    ? pricingMoment
    : currentBatchMoment
  // The pin freezes positions, so a newer committed batch makes it stale even when live pricing
  // is ahead. The moment guard ignores a batch id regressed by a stale response.
  const pinnedBatchSuperseded = isPinnedHistoricalBatch(intradayBatchMetadata?.id, lastIntradayBatch?.id)
    && Boolean(currentBatchMoment && lastBatchMoment?.isAfter(currentBatchMoment))
  const newDataAvailable = isIntradayEnabled
    && Boolean(lastBatchMoment)
    && (!currentlyShownMoment || (lastBatchMoment?.isAfter(currentlyShownMoment) ?? false) || pinnedBatchSuperseded)

  // "Latest snapshot" means whichever is actually latest, not always the batch.
  const batchIsLatestSnapshot = isBatchNewerThanPricing(lastIntradayBatch, intradayPricingAsOf)
  // Stable reference so refreshIntradayData/the auto-refresh effect don't churn on every price tick.
  const latestSnapshotBatch = useMemo(() => (
    batchIsLatestSnapshot ? { asOf: lastIntradayBatch?.asOf, date: lastIntradayBatch?.date, id: lastIntradayBatch?.id } : null
  ), [batchIsLatestSnapshot, lastIntradayBatch?.asOf, lastIntradayBatch?.date, lastIntradayBatch?.id])
  const latestSnapshotMoment = batchIsLatestSnapshot ? lastBatchMoment : pricingMoment

  const shouldAutoRefresh = useMemo(() => {
    return newDataAvailable && useLatestDate && isIntradayBatchAvailable && trades?.length === 0 && isPreferencesLoaded && !hasOptimizationComparison
  }, [newDataAvailable, useLatestDate, isIntradayBatchAvailable, trades, isPreferencesLoaded, hasOptimizationComparison])

  const refreshIntradayData = useCallback(() => {
    setIntradayBatchMetadata(latestSnapshotBatch)
    setUseLatestDate(true)
    setPeriod({
      period: currentPeriod,
      startDate: adjustedStartDate,
      endDate: intradayDate,
    })
  }, [setIntradayBatchMetadata, latestSnapshotBatch, setUseLatestDate, currentPeriod, setPeriod, adjustedStartDate, intradayDate])

  const onRefreshPositions = useCallback((hasTrades: boolean, hasOptimizationComparison: boolean) => {
    if (hasTrades) {
      resetTrades()
    }
    if (hasOptimizationComparison) {
      setComparisonInstrument(null)
      setIsOptimizePanelExpanded(false)
    }
    setIntradayBatchMetadata(latestSnapshotBatch)
    setPeriod({
      period: currentPeriod,
      startDate: adjustedStartDate,
      endDate: intradayDate,
    })
  }, [resetTrades, setComparisonInstrument, setIsOptimizePanelExpanded, setIntradayBatchMetadata, latestSnapshotBatch, adjustedStartDate, intradayDate, currentPeriod, setPeriod])

  const showRefreshDialog = useCallback((
    config: {
      hasTrades: boolean
      hasOptimizationComparison: boolean
    },
    onConfirm: (hasTrades: boolean, hasOptimizationComparison: boolean) => void,
    onDownload: () => { fileName: string, data: { sheets: DownloadSheet[] } | null },
    newAsOf?: string,
  ) => {
    const { hasTrades, hasOptimizationComparison } = config
    const actions: string[] = []

    if (hasTrades) {
      actions.push("reset your trades")
    }

    if (hasOptimizationComparison) {
      actions.push("remove your optimization strategy")
    }

    const contentText = `There is new intraday data available as of ${newAsOf}. Refreshing will ${actions.join(" and ")}. Would you still like to proceed?`
    dispatch(DialogCreators.open({
      title: "Confirm Refresh and Clear",
      contentText,
      buttons: [{
        text: "Download & Refresh",
        type: "secondary",
        action: async () => {
          const { data, fileName } = onDownload()
          await downloadXlsx(fileName, data, {
            type: "intraday-refresh-download",
            location: "dashboard",
          })
          onConfirm(hasTrades, hasOptimizationComparison)
        },
      }, {
        text: "Refresh",
        type: "primary",
        action: () => onConfirm(hasTrades, hasOptimizationComparison),
      }],
    }))
  }, [dispatch])

  const handleIntradayRefresh = useCallback(() => {
    const hasTrades = trades?.length > 0

    if (hasTrades || hasOptimizationComparison) {
      const newAsOf = latestSnapshotMoment?.format("LTS")
      showRefreshDialog(
        { hasTrades, hasOptimizationComparison },
        onRefreshPositions,
        onDownloadData,
        newAsOf,
      )
    } else if (isOptimizeExperimentsEnabled) {
      setIsOptimizePanelExpanded(false)
      refreshIntradayData()
    } else {
      refreshIntradayData()
    }
  }, [trades, onRefreshPositions, onDownloadData, latestSnapshotMoment, refreshIntradayData, showRefreshDialog, hasOptimizationComparison, isOptimizeExperimentsEnabled, setIsOptimizePanelExpanded])

  useEffect(() => {
    if (shouldAutoRefresh) {
      refreshIntradayData()
    }
  }, [shouldAutoRefresh, refreshIntradayData])

  // Determine if we should show the manual refresh button
  const showRefreshButton = useMemo(() => {
    // Show refresh button if:
    // 1. New data is available and won't auto-refresh, OR
    // 2. Viewing historical dates with intraday batch available
    const newDataNeedsRefresh = newDataAvailable && !shouldAutoRefresh
    const historicalWithIntradayData = isIntradayBatchAvailable && !isEndDateToday
    return newDataNeedsRefresh || historicalWithIntradayData
  }, [newDataAvailable, shouldAutoRefresh, isIntradayBatchAvailable, isEndDateToday])

  const refreshButtonTooltip = useMemo(() => {
    const newDataNeedsRefresh = newDataAvailable && !shouldAutoRefresh
    if (newDataNeedsRefresh) {
      return "New intraday data available"
    }
    return "Load intraday data as end date"
  }, [newDataAvailable, shouldAutoRefresh])

  // Determine if we should show the warning icon
  const showWarning = useMemo(() => {
    if (isIntradayAnalyticsEnabled) return false
    return isIntradayEnabled && !isIntradayBatchAvailable && lastIntradayBatchStatus !== LOADING
  }, [isIntradayAnalyticsEnabled, isIntradayEnabled, isIntradayBatchAvailable, lastIntradayBatchStatus])

  return {
    newDataAvailable,
    showRefreshButton,
    showWarning,
    handleIntradayRefresh,
    refreshIntradayData,
    refreshButtonTooltip,
  }
}
